/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useCallback } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, CircularProgress, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import StepOneUpdate from './contractUpdateSteps/StepOne';
import StepTwoUpdate from './contractUpdateSteps/StepTwo';
import StepThreeUpdate from './contractUpdateSteps/StepThree';
import StepFourUpdate from './contractUpdateSteps/StepFour';
import StepFiveUpdate from './contractUpdateSteps/StepFive';
import StepSixUpdate from './contractUpdateSteps/StepSix';
import StepSevenUpdate from './contractUpdateSteps/StepSeven';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../Config';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
   setContractUpdatePrefilledData,
   clearContractTypePreFill,
} from '../../Redux/Reducers/Contracts/ContractSlice';
import { getAuthToken } from '../../Redux/Service/FetchBaseQuery';
import dayjs from 'dayjs';
import StyledDashboardLayout from '../style';

// Steps for the stepper
const steps = [
   'Contract Setup',
   'Commercial Details & Project Development',
   'Technology Stack',
   // 'Project Development Time',
   'Bank Details',
   'Terms & Conditions',
   'Upload Signature',
];

export default function ContractUpdate() {
   // State management - use useState to prevent infinite loops
   const [activeStep, setActiveStep] = useState(0);
   const [skipped, setSkipped] = useState(new Set());
   const [contractData, setContractData] = useState(null);
   const [contractTitle, setContractTitle] = useState('loading...');
   const [isFormSubmitting, setIsFormSubmitting] = useState(false); // Renamed to avoid confusion with loading states
   const [dataFetched, setDataFetched] = useState(false);
   const [deletedTeamIds, setDeletedTeamIds] = useState([]);
   const [deletedDmIds, setDeletedDmIds] = useState([]);

   // Restoration Refs & State
   const formValuesRef = React.useRef(null);
   const setFieldValueRef = React.useRef(null);
   const formikRef = React.useRef(null);
   const [shouldRestore, setShouldRestore] = React.useState(false);

   // Tracks which contract ID has already been prefilled into the form.
   // Lives as a ref here (not inside StepOneUpdate) so it survives step navigation
   // unmount/remount cycles and never re-triggers the DB prefill after a manual type change.
   const loadedContractIdRef = React.useRef(null);

   // Hooks
   const navigate = useNavigate();
   const dispatch = useDispatch();
   const { id } = useParams();

   // Clean Redux store before redirect
   // After success
   const cleanupAndRedirectToContractList = useCallback(async () => {
      dispatch(clearContractTypePreFill());
      dispatch(setContractUpdatePrefilledData(null));

      loadedContractIdRef.current = null;
      formValuesRef.current = null;
      setFieldValueRef.current = null;

      navigate('/contract');
   }, [dispatch, navigate]);

   // Cleanup contract type pre-fill state on unmount
   React.useEffect(() => {
      return () => {
         dispatch(clearContractTypePreFill());
         dispatch(setContractUpdatePrefilledData(null));
      };
   }, [dispatch]);

   // Restoration Effect: Runs after re-render to restore form data on failure
   React.useEffect(() => {
      if (shouldRestore && formValuesRef.current && setFieldValueRef.current) {
         Object.keys(formValuesRef.current).forEach((key) => {
            setFieldValueRef.current(key, formValuesRef.current[key], false);
         });
         setShouldRestore(false);
      }
   }, [shouldRestore]);

   // Redux States - use memo to prevent unnecessary re-renders
   const quotations = useSelector((state) => state.contractState.quotation);
   const contractUpdatePrefilledData = useSelector(
      (state) => state.contractState.contractUpdatePrefilledData,
   );
   const signaturePath = useSelector(
      (state) => state.contractState.prefilledData?.response?.[7]?.data?.signature || '',
   );

   const maxWithoutSpaces = (label, max = 5000) =>
      Yup.string()
         .required(`${label} is required`)
         .test(
            'max-without-spaces',
            `${label} is too long. Please keep it within ${max} characters`,
            (value = '') => value.replace(/\s/g, '').length <= max
         );

   // Fetch contract data only once when component mounts
   useEffect(() => {
      // Only fetch if we haven't already
      if (!dataFetched) {
         const fetchContractData = async () => {
            try {
               const token = getAuthToken();
               const response = await axios.get(`${API_URL}contract/view/${id}`, {
                  headers: {
                     Authorization: `Bearer ${token}`,
                     token,
                  },
               });

               if (response?.data?.response) {
                  setContractData(response.data.response);

                  dispatch(setContractUpdatePrefilledData(response.data.response));

                  // Use the quotation title if available, otherwise use a fallback title
                  const title =
                     response.data.response?.quotation?.title ||
                     response.data.response?.opportunity?.name ||
                     'Contract';
                  setContractTitle(title);
               }
               setDataFetched(true);
            } catch (error) {
               console.error('Error fetching contract data:', error);
               toast.error('Something went wrong while fetching contract data');
            }
         };

         fetchContractData();
      }
   }, [dispatch, id, dataFetched]); // Only depend on these values

   // Validation schema - keep this outside the component or memoize it
   const validationSchema = Yup.object().shape({
      date: Yup.date().nullable(),
      projectName: Yup.string().required('Project Name is required').max(100, "Project name should be less than 100 characters"),
      serviceProviderId: Yup.number()
         .typeError('Service Provider is required')
         .required('Service Provider is required'),
      project_development: Yup.string().required().max(100, "Project development is too long. Please keep it within 100 characters"),
      note: Yup.string().max(1000, "Notes is too long. Please keep it within 1000 characters").required("Note is required"),
      confidential_information: Yup.string().required().max(5000, "Confidential Information is too long. Please keep it within 5000 characters"),
      purpose_of_the_agreement: Yup.string().required().max(1000, "Purpose of the agreement is too long. Please keep it within 1000 characters"),
      scope_of_work: Yup.string().required().max(1000, "Scope of work is too long. Please keep it within 1000 characters"),
      amendments: Yup.string().required().max(5000, "Amendments is too long. Please keep it within 5000 characters"),
      commencementValue: Yup.number().required('Commencement value is required').min(1, "Commencement value should be greater than 0").max(999999, "Commencement value should be atmost 6 digits"),
      commencementLabel: Yup.string().max(50, "Commencement label is too long. Please keep it within 50 characters").required("Commencement label is required"),
      total_development_cost: Yup.number().min(1, "Total Development Cost must be greater than 0").required('Total Development Cost is required').max(99999999, "Total Development Cost should be atmost 8 digits"),

      Bank_Details: Yup.array()
         .of(Yup.number().typeError('Each bank id must be a number'))
         .min(1, 'At least one bank account is required'),
      general_terms_and_conditions: maxWithoutSpaces('General Terms and Conditions'),
      project_termination_terms: maxWithoutSpaces('Project Termination Terms'),
      payment_terms: maxWithoutSpaces('Payment Terms'),
      application_ownership: maxWithoutSpaces('Application Ownership'),
      liability: maxWithoutSpaces('Liability'),
      legal_jurisdiction: maxWithoutSpaces('Legal Jurisdiction'),
      // address: Yup.string(), // <-- Remove required
      client_name: Yup.string().required().max(100, "Client Name is too long. Please keep it within 100 characters"),
      address: Yup.string().required().max(250, "Client Address is too long. Please keep it within 250 characters"),
      designation: Yup.string().required().max(250, "Designation is too long. Please keep it within 250 characters"),
      companyName: Yup.string().required().max(250, "Company Name is too long. Please keep it within 250 characters"),
      deliverable_milestones: Yup.array().of(
         Yup.object().shape({
            milestone: Yup.string()
               .required('Milestone name is required')
               .max(50, 'Milestone name should be within 50 characters'),
            cost: Yup.number()
               .typeError('Cost must be a number')
               .moreThan(0, 'Cost must be greater than 0')
               .max(999999, 'Cost should have at most 6 digits')
               .required('Cost is required'),
         }),
      ),

      // NEW: Team Structure validation
      team_structure: Yup.array().of(
         Yup.object().shape({
            member: Yup.string()
               .max(50, 'Team name should be within 50 characters'),
            engagement: Yup.string()
               .max(50, 'Engagement team should be within 50 characters'),
            count: Yup.number()
               .typeError('Number of team members must be a number')
               .max(999, 'Number of team members cannot exceed 999')
               .nullable(),
         }).test(
            'team-row-required-together',
            'Team name, engagement, and count are required for this row',
            function (item) {
               const { member, engagement, count } = item || {}
               const anyFilled =
                  (member && member.trim() !== '') ||
                  (engagement && engagement.trim() !== '') ||
                  (count !== undefined && count !== null && String(count).trim() !== '' && Number(count) > 0)

               if (!anyFilled) return true

               const memberOk = !!(member && member.trim() !== '')
               const engagementOk = !!(engagement && engagement.trim() !== '')
               const countOk =
                  count !== undefined && count !== null &&
                  String(count).trim() !== '' && Number(count) > 0

               return memberOk && engagementOk && countOk
            },
         ),
      ),
      dm_structure: Yup.array().of(
         Yup.object().shape({
            milname: Yup.string()
               .max(50, 'Milestone name should be within 50 characters'),
            delivery: Yup.number()
               .typeError('Delivery must be a number')
               .max(999, 'Delivery value cannot exceed 999')
               .nullable(),
            type: Yup.string(),
         }).test(
            'dm-row-required-together',
            'Milestone name and delivery are required for this row',
            function (item) {
               const { milname, delivery, type } = item || {}
               const anyFilled =
                  (milname && milname.trim() !== '') ||
                  (delivery !== undefined && delivery !== null && String(delivery).trim() !== '' && Number(delivery) > 0) ||
                  (type && type.trim() !== '')
               if (!anyFilled) return true
               const milnameOk = !!(milname && milname.trim() !== '')
               const deliveryOk =
                  delivery !== undefined && delivery !== null &&
                  String(delivery).trim() !== '' && Number(delivery) > 0
               return milnameOk && deliveryOk
            },
         ),
      ),
   });

   // Step navigation functions
   const isStepOptional = (step) => step === 1;
   const isStepSkipped = (step) => skipped.has(step);

   const hasMeaningfulEditorContent = (value) => {
      if (!value) return false;
      const container = document.createElement('div');
      container.innerHTML = String(value).replace(/&nbsp;/g, ' ');
      const plainText = (container.textContent || '').replace(/\s+/g, ' ').trim();
      return plainText.length > 0;
   };

   const handleNext = useCallback(
      (values) => {
         // Add validation for each step before moving to next
         if (activeStep === 0) {
            if (!values.contractTypeId) {
               toast.error('Please select a contract type');
               return;
            } else if (!values.lead_id) {
               toast.error('Please select an opportunity');
               return;
            } else if (!values.serviceProviderId) {
               toast.error('Please select a service provider');
               return;
            } else if (!hasMeaningfulEditorContent(values.confidential_information)) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('confidential_information', true);
               }
               toast.error('Please fill the Confidential Information');
               return;
            } else if (!hasMeaningfulEditorContent(values.purpose_of_the_agreement)) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('purpose_of_the_agreement', true);
               }
               toast.error('Please fill the Purpose of the Agreement');
               return;
            } else if (!hasMeaningfulEditorContent(values.scope_of_work)) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('scope_of_work', true);
               }
               toast.error('Please fill the Scope of Work');
               return;
            } else if (!hasMeaningfulEditorContent(values.amendments)) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('amendments', true);
               }
               toast.error('Please fill the Amendments');
               return;
            } else if (!String(values.projectName || '').trim()) {
               toast.error('Please fill the Project Name');
               return;
            } else if (values.confidential_information.length > 5000) {
               toast.error('Confidential Information should have at most 5000 characters');
               return;
            }
            else if (values.amendments.length > 5000) {
               toast.error('Amendment should have at most 5000 characters');
               return;
            }
            else if (values.scope_of_work.length > 1000) {
               toast.error('Scope of Work should have at most 1000 characters');
               return;
            }
            else if (values.purpose_of_the_agreement.length > 1000) {
               toast.error('Purpose of the Agreement should have at most 1000 characters');
               return;
            } else if (values.projectName.length > 100) {
               toast.error('Project Name should have at most 100 characters');
               return;
            } else if (values.scope_of_work.length > 1000) {
               toast.error('Scope of Work should have at most 1000 characters');
               return;
            } else if (values.client_name?.length > 100) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('client_name', true);
               }
               toast.error('Client Name should have at most 100 characters');
               return;
            } else if (values.address?.length > 250) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('address', true);
               }
               toast.error('Client Address should have at most 250 characters');
               return;
            } else if (values.companyName?.length > 250) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('companyName', true);
               }
               toast.error('Company Name should have at most 250 characters');
               return;
            } else if (values.designation?.length > 250) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('designation', true);
               }
               toast.error('Designation should have at most 250 characters');
               return;
            }
         } else if (activeStep === 1) {
            if (formikRef.current) {
               formikRef.current.setTouched({
                  ...formikRef.current.touched,
                  deliverable_milestones: values.deliverable_milestones.map(() => ({
                     milestone: true,
                     cost: true,
                  })),
                  dm_structure: values.dm_structure.map(() => ({
                     milname: true,
                     delivery: true,
                  })),
               });
            }
            if (values.total_development_cost > 99999999) {
               toast.error('Total Development Cost should have at most 8 digits');
               return;
            }
            if (!values.total_development_cost || Number(values.total_development_cost) === 0) {
               toast.error('Please fill the Total Development Cost');
               return;
            }
            if (!values.project_development) {
               toast.error('Please fill the Project Development Time');
               return;
            }
            // Validate deliverable milestones (optional but must be complete if present)
            console.log(values.dm_structure, "dm structure");
            for (let i = 0; i < values.dm_structure.length; i++) {
               const dm = values.dm_structure[i];
               if (!dm.milname) {
                  toast.error(`Deliverable Milestone row ${i + 1}: Please provide Milestone name`);
                  return;
               }
               if (dm.milname?.length > 50) {
                  toast.error(`Deliverable Milestone row ${i + 1}: Milestone name should have at most 50 characters`);
                  return;
               }
               if (dm.milname?.delivery > 999) {
                  toast.error(`Deliverable Milestone row ${i + 1}: Delivery  should have at most 3 digits`);
                  return;
               }
               if (!dm.delivery || Number(dm.delivery) === 0) {
                  toast.error(
                     `Deliverable Milestone row ${i + 1}: Delivery time must be greater than 0`,
                  );
                  return;
               }
            }
            if (values.commencementLabel.length > 50) {
               toast.error('Commencement Label should have at most 50 characters');
               return;
            }
            if (values.commencementValue > 999999) {
               toast.error('Commencement Value should have at most 6 digits');
               return;
            }
            // Commencement Label validation
            if (
               values.commencementLabel === '' ||
               values.commencementLabel === null ||
               values.commencementLabel === undefined
            ) {
               toast.error('Commencement Label is required');
               return;
            }
            // Commencement Value validation
            if (!values.commencementValue || Number(values.commencementValue) === 0) {
               toast.error('Commencement Value is required');
               return;
            }
            // Validate payment milestones (required, must be complete)
            for (let i = 0; i < values.deliverable_milestones.length; i++) {
               const pm = values.deliverable_milestones[i];
               if (!pm.milestone) {
                  toast.error(`Payment Milestone row ${i + 1}: Please provide Milestone name`);
                  return;
               }
               if (pm.milestone?.length > 50) {
                  toast.error(`Payment Milestone row ${i + 1}: Milestone name should be within 50 characters`);
                  return;
               }
               if (pm.cost > 999999) {
                  toast.error(`Payment Milestone row ${i + 1}: Cost should have at most 6 digits`);
                  return;
               }
               if (pm.cost === '' || pm.cost === null || pm.cost === undefined) {
                  toast.error(`Payment Milestone row ${i + 1}: Please provide Cost value`);
                  return;
               }
               if (Number(pm.cost) === 0) {
                  toast.error(`Payment Milestone row ${i + 1}: Cost cannot be zero`);
                  return;
               }
            }
            // Add milestone + commencement validation for StepTwo (step 3 in ContractUpdate)
            const calculatedTaxAmount =
               (values.total_development_cost * (values.payable_tax_percentage || 0)) / 100;
            const totalAmountExcludingTax =
               Number(values.total_development_cost);
            const commencementFee = Number(values.commencementValue || 0);
            const totalMilestoneSum =
               values.deliverable_milestones.reduce(
                  (acc, curr) => acc + Number(curr.cost || 0),
                  0,
               ) + commencementFee;

            if (totalMilestoneSum !== totalAmountExcludingTax) {
               toast.error(
                  'Total (Milestones + Commencement Fee) should match the Total Development Cost including tax',
               );
               return;
            }
            if (!values.note) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('note', true);
               }
               toast.error("Please fill the Note");
               return;
            }
            if (values.note && values.note.length > 1000) {
               toast.error('Note should have at most 1000 characters');
               return;
            }
         } else if (activeStep === 2) {
            if (values.proposed_technology_stack.length === 0) {
               toast.error('Please fill the Proposed Technology Stack');
               return;
            }
            // Validate team structure - if any field is filled, all must be complete
            for (let i = 0; i < values.team_structure.length; i++) {
               const team = values.team_structure[i];
               if (team.member || team.engagement || team.count) {
                  if (!team.member) {
                     toast.error(`Team Structure row ${i + 1}: Please provide Team Name`);
                     return;
                  }
                  if (team.member?.length > 50) {
                     toast.error(`Team Structure row ${i + 1}: Team Name should be within 50 characters`);
                     return;
                  }

                  if (team.engagement?.length > 50) {
                     toast.error(`Team Structure row ${i + 1}: Engagement should be within 50 characters`);
                     return;
                  }

                  if (team.count > 999) {
                     toast.error(`Team Structure row ${i + 1}: Count should have at most 3 digits`);
                     return;
                  }
                  if (!team.engagement) {
                     toast.error(`Team Structure row ${i + 1}: Please provide Engagement Team`);
                     return;
                  }
                  if (!team.count || Number(team.count) === 0) {
                     toast.error(
                        `Team Structure row ${i + 1}: Number of team members must be greater than 0`,
                     );
                     return;
                  }
               }
            }
         }
         // else if (activeStep === 3) {
         // if (!values.project_development) {
         //    toast.error('Please fill the Project Development Time');
         //    return;
         // }
         // // Validate deliverable milestones (optional but must be complete if present)
         // for (let i = 0; i < values.dm_structure.length; i++) {
         //    const dm = values.dm_structure[i];
         //    if (!dm.milname) {
         //       toast.error(`Deliverable Milestone row ${i + 1}: Please provide Milestone name`);
         //       return;
         //    }
         //    if (!dm.delivery || Number(dm.delivery) === 0) {
         //       toast.error(
         //          `Deliverable Milestone row ${i + 1}: Delivery time must be greater than 0`,
         //       );
         //       return;
         //    }
         // }
         // // Commencement Label validation
         // if (
         //    values.commencementLabel === '' ||
         //    values.commencementLabel === null ||
         //    values.commencementLabel === undefined
         // ) {
         //    toast.error('Commencement Label is required');
         //    return;
         // }
         // // Commencement Value validation
         // if (!values.commencementValue || Number(values.commencementValue) === 0) {
         //    toast.error('Commencement Value is required');
         //    return;
         // }
         // // Validate payment milestones (required, must be complete)
         // for (let i = 0; i < values.deliverable_milestones.length; i++) {
         //    const pm = values.deliverable_milestones[i];
         //    if (!pm.milestone) {
         //       toast.error(`Payment Milestone row ${i + 1}: Please provide Milestone name`);
         //       return;
         //    }
         //    if (pm.cost === '' || pm.cost === null || pm.cost === undefined) {
         //       toast.error(`Payment Milestone row ${i + 1}: Please provide Cost value`);
         //       return;
         //    }
         //    if (Number(pm.cost) === 0) {
         //       toast.error(`Payment Milestone row ${i + 1}: Cost cannot be zero`);
         //       return;
         //    }
         // }
         // // Add milestone + commencement validation for StepTwo (step 3 in ContractUpdate)
         // const calculatedTaxAmount =
         //    (values.total_development_cost * (values.payable_tax_percentage || 0)) / 100;
         // const totalAmountIncludingTax =
         //    Number(values.total_development_cost) + Number(calculatedTaxAmount);
         // const commencementFee = Number(values.commencementValue || 0);
         // const totalMilestoneSum =
         //    values.deliverable_milestones.reduce(
         //       (acc, curr) => acc + Number(curr.cost || 0),
         //       0,
         //    ) + commencementFee;

         // if (totalMilestoneSum !== totalAmountIncludingTax) {
         //    toast.error(
         //       'Total (Milestones + Commencement Fee) should match the Total Development Cost including tax',
         //    );
         //    return;
         // }
         // } 
         else if (activeStep === 3) {
            if (!values.Bank_Details || values.Bank_Details.length === 0) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('Bank_Details', true);
               }
               toast.error('Please select at least one bank account');
               return;
            }
         } else if (activeStep === 4) {
            const lengthWithoutSpaces = (value = '') =>
               String(value).replace(/\s/g, '').length;
            const fields = [
               ['general_terms_and_conditions', 'General Terms and Conditions'],
               ['project_termination_terms', 'Project Termination Terms'],
               ['payment_terms', 'Payment Terms'],
               ['application_ownership', 'Application Ownership'],
               ['liability', 'Liability'],
               ['legal_jurisdiction', 'Legal Jurisdiction'],
            ];

            for (const [field, label] of fields) {
               if (!values[field]) {
                  if (formikRef.current) {
                     formikRef.current.setFieldTouched(field, true);
                  }
                  toast.error(`Please fill the ${label}`);
                  return;
               }

               if (lengthWithoutSpaces(values[field]) > 5000) {
                  toast.error(`${label} should have at most 5000 characters`);
                  return;
               }
            }
         }

         if (activeStep < steps.length) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
         }
      },
      [activeStep, steps.length],
   );

   const handleBack = useCallback(() => {
      if (activeStep > 0) {
         setActiveStep((prevActiveStep) => prevActiveStep - 1);
      }
   }, [activeStep]);

   // Create initial values object - memoize to prevent regeneration on each render
   const initialValues = React.useMemo(() => {
      const vals = {
         date: null, // Changed from new Date() to null to prevent invalid date errors
         confidential_information: ``,
         lead_id: '',
         quotation_id: '',
         purpose_of_the_agreement: '',
         scope_of_work: '',
         currency_id: null, // Changed to number (optional)
         amendments: '',
         project_development: '',
         commencementLabel: '',
         commencementValue: '',
         projectName: '',
         client_name: '',
         companyName: '',
         note: '',
         designation: '',
         address: '',
         deliverable_milestones: [
            {
               id: 1,
               milestone: '',
               // type: "",
               // delivery: 0,
               cost: 0, // Already number type
            },
         ],
         proposed_technology_stack:
            contractUpdatePrefilledData?.techStack?.map((tech) => tech.technology) || [],
         team_structure:
            contractUpdatePrefilledData?.team && contractUpdatePrefilledData.team.length > 0
               ? contractUpdatePrefilledData.team
               : [
                  {
                     id: 1,
                     member: '',
                     engagement: '',
                     count: 0,
                  },
               ],
         dm_structure: contractUpdatePrefilledData?.deliverable || [],
         total_development_cost: 0,
         payable_tax_percentage: 0,
         Bank_Details: [], // Will be converted to number array
         general_terms_and_conditions: '',
         project_termination_terms: '',
         payment_terms: '',
         application_ownership: '',
         liability: '',
         legal_jurisdiction: '',
         uploadedFile: [''],
         signature: '', // Only new uploads, not existing images
         removedOldFiles: [], // Track which old milestone images user wants to remove
         removedOldSignature: false, // Track if user wants to remove existing signature
         serviceProviderId: contractUpdatePrefilledData?.serviceProvider?.id || '',
         totalTime: 0, // New optional field
         disclaimer: '', // New optional field
         contractTypeId: contractUpdatePrefilledData?.contract_type_id || '',
      };
      return vals;
   }, [contractUpdatePrefilledData]);

   // Helper: extract text array from HTML produced by CKEditor
   // (moved above handleSubmit so update-time logic always uses this robust extractor)
   const extractHtmlListItems = (html) => {
      if (!html) return [];
      // Use DOM parsing to preserve list / paragraph structure
      // Note: window/document available in browser environment
      const container = document.createElement('div');
      container.innerHTML = String(html);

      // Prefer li elements if present
      const liNodes = container.querySelectorAll('li');
      if (liNodes.length) {
         return Array.from(liNodes)
            .map((n) => n.textContent.trim())
            .filter(Boolean);
      }

      // Otherwise use paragraphs if present
      const pNodes = container.querySelectorAll('p');
      if (pNodes.length) {
         return Array.from(pNodes)
            .map((n) => n.textContent.trim())
            .filter(Boolean);
      }

      // Fallback: get textContent and split into sentences (keeps punctuation)
      const text = container.textContent || '';
      const normalized = text.replace(/\s+/g, ' ').trim();
      if (!normalized) return [];
      const sentences = normalized.match(/[^.!?]+[.!?]*/g) || [normalized];
      return sentences.map((s) => s.trim()).filter(Boolean);
   };

   // Helper: normalize prefilled image names coming from API (same as StepSeven)
   function normalizePrefilledImageNames(rawImageField) {
      if (!rawImageField) return [];
      if (Array.isArray(rawImageField) && rawImageField.every((it) => typeof it === 'string'))
         return rawImageField;
      if (Array.isArray(rawImageField)) {
         const out = [];
         for (const item of rawImageField) {
            if (!item) continue;
            if (typeof item === 'string') out.push(item);
            else if (Array.isArray(item?.image)) out.push(...item.image);
            else if (typeof item?.image === 'string') out.push(item.image);
         }
         return out;
      }
      if (typeof rawImageField === 'object') {
         if (Array.isArray(rawImageField.image)) return rawImageField.image.slice();
         if (typeof rawImageField.image === 'string') return [rawImageField.image];
      }
      return [];
   }

   // Fetch a remote URL and return as a File instance
   async function fetchUrlAsFile(url, filename) {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch file: ${url}`);
      const blob = await res.blob();
      const type = blob.type || 'application/octet-stream';
      return new File([blob], filename, { type });
   }

   // Handle form submission
   const handleSubmit = async (values) => {
      // Backup values immediately upon submission
      formValuesRef.current = { ...values };

      // Validate team structure completeness
      for (let i = 0; i < values.team_structure.length; i++) {
         const team = values.team_structure[i];
         if (team.member || team.engagement || team.count) {
            if (!team.member) {
               toast.error(`Team Structure row ${i + 1}: Please provide Team Name`);
               return;
            }
            if (!team.engagement) {
               toast.error(`Team Structure row ${i + 1}: Please provide Engagement Team`);
               return;
            }
            if (!team.count || Number(team.count) === 0) {
               toast.error(
                  `Team Structure row ${i + 1}: Number of team members must be greater than 0`,
               );
               return;
            }
         }
      }

      // Validate deliverable milestones completeness
      for (let i = 0; i < values.dm_structure.length; i++) {
         const dm = values.dm_structure[i];
         if (!dm.milname) {
            toast.error(`Deliverable Milestone row ${i + 1}: Please provide Milestone name`);
            return;
         }
         if (!dm.delivery || Number(dm.delivery) === 0) {
            toast.error(`Deliverable Milestone row ${i + 1}: Delivery time must be greater than 0`);
            return;
         }
      }

      // Validate payment milestones completeness
      for (let i = 0; i < values.deliverable_milestones.length; i++) {
         const pm = values.deliverable_milestones[i];
         if (!pm.milestone) {
            toast.error(`Payment Milestone row ${i + 1}: Please provide Milestone name`);
            return;
         }
         if (pm.cost === '' || pm.cost === null || pm.cost === undefined) {
            toast.error(`Payment Milestone row ${i + 1}: Please provide Cost value`);
            return;
         }
         if (Number(pm.cost) === 0) {
            toast.error(`Payment Milestone row ${i + 1}: Cost cannot be zero`);
            return;
         }
      }

      // Remove duplicate validation from here since it's now in handleNext
      const calculatedTaxAmount =
         (values.total_development_cost * (values.payable_tax_percentage || 0)) / 100;
      const totalAmountIncludingTax =
         Number(values.total_development_cost) + Number(calculatedTaxAmount);
      const commencementFee = Number(values.commencementValue || 0);

      if (!values.serviceProviderId) {
         toast.error('Please select a service provider');
         return;
      }

      setIsFormSubmitting(true);
      try {
         const contractFormData = new FormData();

         // SIGNATURE: Extract prefilled signature name first
         const prefilledImages = normalizePrefilledImageNames(contractUpdatePrefilledData?.image);
         const prefilledSignatureName =
            prefilledImages.find((img) => typeof img === 'string' && img.includes('signature')) ||
            contractUpdatePrefilledData?.signature ||
            null;

         // SIGNATURE: Handle three cases
         // 1. New file uploaded -> send new file
         // 2. Old signature kept -> re-attach existing
         // 3. Signature removed -> send empty string to delete
         if (values.signature && values.signature instanceof File) {
            contractFormData.append('signature', values.signature);
         } else if (values.removedOldSignature) {
            // User explicitly removed the signature -> send empty string to delete
            contractFormData.append('signature', '');
         } else if (!values.removedOldSignature && prefilledSignatureName) {
            try {
               const signatureFilename = prefilledSignatureName.includes('/')
                  ? prefilledSignatureName.split('/').pop()
                  : prefilledSignatureName;
               const sigFile = await fetchUrlAsFile(
                  `${API_URL}signature/${signatureFilename}`,
                  signatureFilename,
               );
               contractFormData.append('signature', sigFile);
            } catch (e) {
               console.warn('Failed to re-attach existing signature', e);
            }
         }
         // If none of the above, signature is not appended (undefined) - backend preserves existing

         contractFormData.append('opportunity', values.lead_id ? Number(values.lead_id) : null);

         contractFormData.append(
            'contract_type_id',
            values.contractTypeId ? Number(values.contractTypeId) : null,
         );

         contractFormData.append(
            'quotation',
            values.quotation_id ? Number(values.quotation_id) : null,
         );
         contractFormData.append(
            'confidentialInfo',
            values.confidential_information
               ? normalizeEditorContent(values.confidential_information)
               : '',
         );
         contractFormData.append(
            'purposeAgreement',
            values.purpose_of_the_agreement
               ? normalizeEditorContent(values.purpose_of_the_agreement)
               : '',
         );
         contractFormData.append(
            'scopeWork',
            values.scope_of_work
               ? normalizeEditorContent(values.scope_of_work) // keep CKEditor HTML
               : '',
         );
         contractFormData.append(
            'amendment',
            values.amendments ? normalizeEditorContent(values.amendments) : '',
         );
         // Add These data in data base
         contractFormData.append('proposal', values.proposal ? Number(values.proposal) : null);
         contractFormData.append('estimate', values.estimate ? Number(values.estimate) : null);
         contractFormData.append('milestones', values.milestone ? Number(values.milestone) : null);
         contractFormData.append(
            'techStack',
            JSON.stringify(values.proposed_technology_stack || []),
         );

         // Filter out empty team entries (where member, engagement, and count are all empty/zero)
         const filteredTeam = values.team_structure
            .filter((team) => team.member || team.engagement || team.count)
            .map((team) => ({
               ...team,
               count: Number(team.count) || 0, // Ensure count is a number
            }));
         contractFormData.append('team', JSON.stringify(filteredTeam));

         contractFormData.append('deletedTeamIds', JSON.stringify(deletedTeamIds || []));

         contractFormData.append('deletedDmIds', JSON.stringify(deletedDmIds || []));

         contractFormData.append('dm', JSON.stringify(values.dm_structure || []));

         contractFormData.append(
            'milestone',
            JSON.stringify(
               (values.deliverable_milestones || []).map((m) => ({
                  ...m,
                  cost: Number(m.cost) || 0, // Ensure cost is a number
                  id: m.id ? Number(m.id) : undefined, // Convert id to number if present
               })),
            ),
         );

         // Convert bank array: ensure each element is a number
         const bankArray = (values.Bank_Details || []).map((b) =>
            typeof b === 'number' ? b : Number(b),
         );
         contractFormData.append('bank', JSON.stringify(bankArray));

         contractFormData.append(
            'currencyId',
            values.currency_id ? Number(values.currency_id) : '',
         );

         // General Terms -> send as array (matches Add API)
         contractFormData.append(
            'general_term',
            JSON.stringify(extractHtmlListItems(values.general_terms_and_conditions || '')),
         );

         contractFormData.append('taxAmount', Number(calculatedTaxAmount || 0));

         // Termination Terms -> send as array
         contractFormData.append(
            'termination_term',
            JSON.stringify(extractHtmlListItems(values.project_termination_terms || '')),
         );

         // Payment Terms -> send as array
         contractFormData.append(
            'payment_term',
            JSON.stringify(extractHtmlListItems(values.payment_terms || '')),
         );

         contractFormData.append(
            'app_ownership',
            normalizeEditorContent(values.application_ownership || ''),
         );
         contractFormData.append('liability', normalizeEditorContent(values.liability || ''));
         contractFormData.append(
            'legal_jurisdiction',
            normalizeEditorContent(values.legal_jurisdiction || ''),
         );

         contractFormData.append('billableAmount', Number(values.total_development_cost || 0));

         contractFormData.append(
            'developementTime',
            normalizeEditorContent(values.project_development || ''),
         );
         contractFormData.append('commencementLabel', values.commencementLabel || '');
         contractFormData.append('note', values.note || '');
         contractFormData.append(
            'commencementValue',
            values.commencementValue ? Number(values.commencementValue) : 0,
         );
         contractFormData.append('projectName', values.projectName || '');
         contractFormData.append('client_name', values.client_name || '');
         contractFormData.append('companyName', values.companyName || '');
         contractFormData.append('designation', values.designation || '');
         contractFormData.append('address', values.address || '');

         // Handle milestone file uploads - send new uploads, and if missing, re-attach existing ones unless removed
         const allPrefilled = normalizePrefilledImageNames(contractUpdatePrefilledData?.image);
         const prefilledMilestoneNames = allPrefilled.filter(
            (img) => typeof img === 'string' && !img.includes('signature'),
         );
         const uploaded = Array.isArray(values.uploadedFile) ? values.uploadedFile : [];
         const maxSlots = Math.max(prefilledMilestoneNames.length, uploaded.length);

         for (let i = 0; i < maxSlots; i++) {
            // Skip if user marked the old one as removed
            if (Array.isArray(values.removedOldFiles) && values.removedOldFiles[i]) continue;

            const maybeFile = uploaded[i];
            if (maybeFile && maybeFile instanceof File) {
               contractFormData.append(`image${i}`, maybeFile);
            } else if (prefilledMilestoneNames[i]) {
               try {
                  const fileFromUrl = await fetchUrlAsFile(
                     `${API_URL}MilestoneImage/${prefilledMilestoneNames[i]}`,
                     prefilledMilestoneNames[i],
                  );
                  contractFormData.append(`image${i}`, fileFromUrl);
               } catch (e) {
                  console.warn(
                     'Failed to re-attach existing milestone image',
                     prefilledMilestoneNames[i],
                     e,
                  );
               }
            }
         }

         contractFormData.append('serviceProviderId', Number(values.serviceProviderId));
         contractFormData.append('totalTime', Number(values.totalTime) || 0);
         contractFormData.append('disclaimer', values.disclaimer || '');

         // Submit form data
         const token = getAuthToken();

         const response = await axios.post(`${API_URL}contract/update/${id}`, contractFormData, {
            headers: {
               'Content-Type': 'multipart/form-data',
               Authorization: `Bearer ${token}`,
               token,
            },
         });

         // Generate PDF
         await axios.post(
            `${API_URL}contract/generate/${response.data.response.id}`,
            {},
            {
               headers: {
                  Authorization: `Bearer ${token}`,
                  token,
               },
            },
         );

         toast.success('Contract updated successfully');
         await cleanupAndRedirectToContractList();
      } catch (error) {
         console.error('Contract update error:', error);
         toast.error(error?.response?.data?.message || 'Error updating contract');
         // Trigger restoration for the next render
         setShouldRestore(true);
      } finally {
         setIsFormSubmitting(false);
      }
   };

   // Memoize StepContent to prevent unnecessary re-renders
   const StepContent = useCallback(
      ({ isSubmitting, values, setFieldValue, touched, errors, setFieldTouched, handleBlur }) => {
         switch (activeStep) {
            case 0:
               return (
                  <StepOneUpdate
                     {...{
                        isSubmitting,
                        values,
                        setFieldValue,
                        touched,
                        errors,
                        setFieldTouched,
                        initialValues,
                        handleBlur,
                        activeStep, // Pass activeStep for step navigation tracking
                        loadedContractIdRef, // Persistent ref — survives step unmounts
                     }}
                  />
               );
            case 1:
               return (
                  <StepFourUpdate
                     {...{
                        isSubmitting,
                        values,
                        setFieldValue,
                        touched,
                        errors,
                        initialValues,
                        handleBlur,
                     }}
                  />
               );
            case 2:
               return (
                  <StepThreeUpdate
                     {...{
                        isSubmitting,
                        values,
                        setFieldValue,
                        touched,
                        errors,
                        initialValues,
                        handleBlur,
                        setDeletedTeamIds,
                     }}
                  />
               );
            case 3:
               return (
                  <StepFiveUpdate
                     {...{
                        isSubmitting,
                        values,
                        setFieldValue,
                        touched,
                        errors,
                        initialValues,
                        handleBlur,
                     }}
                  />
               );
            case 4:
               return (
                  <StepSixUpdate
                     {...{
                        isSubmitting,
                        values,
                        setFieldValue,
                        touched,
                        errors,
                        initialValues,
                        handleBlur,
                     }}
                  />
               );
            case 5:
               return (
                  <StepSevenUpdate
                     {...{
                        isSubmitting,
                        values,
                        setFieldValue,
                        touched,
                        errors,
                        initialValues,
                        handleBlur,
                     }}
                  />
               );
            // case 6:
            //    return (
            //       <StepSevenUpdate
            //          {...{
            //             isSubmitting,
            //             values,
            //             setFieldValue,
            //             touched,
            //             errors,
            //             initialValues,
            //             handleBlur,
            //          }}
            //       />
            //    );
            default:
               return null;
         }
      },
      [activeStep, initialValues],
   );

   // Add a function to handle home button click
   const handleHomeClick = useCallback(async () => {
      if (activeStep === 0) {
         await cleanupAndRedirectToContractList();
      } else {
         setActiveStep((prevStep) => prevStep - 1);
      }
   }, [activeStep, cleanupAndRedirectToContractList]);

   // Guard: Check if navigated from ContractList
   React.useEffect(() => {
      const redirectIfInvalidSource = async () => {
         if (!window.contractListSource) {
            await cleanupAndRedirectToContractList();
         }
      };

      redirectIfInvalidSource();
   }, [cleanupAndRedirectToContractList]);

   if (!window.contractListSource) {
      return null;
   }

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between mb-2">
                     <div className="pageTitle">
                        <h1 className="m-0">Update Contract</h1>
                     </div>
                     <Button
                        onClick={handleHomeClick}
                        startIcon={<ArrowBackIcon />}
                        sx={{
                           display: 'flex',
                           alignItems: 'center',
                           gap: '8px',
                           padding: '10px 20px',
                           minWidth: '160px',
                           height: '44px',
                           backgroundColor: '#3F30FF',
                           borderRadius: '8px',
                           color: '#ffffff',
                           border: '2px solid #3F30FF',
                           textTransform: 'none',
                           fontSize: '14px',
                           fontWeight: '600',
                           letterSpacing: '0.5px',
                           boxShadow: '0 4px 12px rgba(63, 48, 255, 0.4)',
                           cursor: 'pointer',
                           transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                           position: 'relative',
                           overflow: 'hidden',
                           '&:before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: '-100%',
                              width: '100%',
                              height: '100%',
                              background:
                                 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                              transition: 'left 0.5s',
                           },
                           '&:hover': {
                              backgroundColor: '#3F30FF',
                              borderColor: '#3F30FF',

                              '&:before': {
                                 left: '100%',
                              },
                           },
                           '& .MuiButton-startIcon': {
                              marginRight: '8px',
                              fontSize: '18px',
                              color: '#ffffff',
                              transition: 'transform 0.3s ease',
                           },
                           '&:hover .MuiButton-startIcon': {
                              transform: 'translateX(-2px)',
                           },
                        }}
                     >
                        {activeStep === 0
                           ? 'Back to Contracts'
                           : `Back to ${steps[activeStep - 1]}`}
                     </Button>
                  </div>
               </div>
            </div>
            <div className="card customCard p-3 m-3">
               <Box sx={{ width: '100%' }}>
                  <Stepper activeStep={activeStep} sx={{ marginBottom: '20px' }}>
                     {steps.map((label, index) => {
                        const stepProps = {};
                        const labelProps = {};

                        if (index === activeStep) {
                           labelProps.sx = {
                              color: '#3F30FF', // Changed to match ContractAdd
                              fontWeight: 'bold',
                           };
                        }
                        return (
                           <Step
                              key={label}
                              {...stepProps}
                              onClick={() => {
                                 if (index < activeStep) {
                                    setActiveStep(index);
                                    setSkipped(new Set());
                                 }
                              }}
                              sx={{
                                 cursor: index < activeStep ? 'pointer' : 'default',
                                 '&:hover': {
                                    backgroundColor: index < activeStep ? '#f5f5f5' : 'transparent',
                                 },
                              }}
                           >
                              <StepLabel {...labelProps}>{label}</StepLabel>
                           </Step>
                        );
                     })}
                  </Stepper>

                  <Formik
                     innerRef={formikRef}
                     initialValues={initialValues}
                     validationSchema={validationSchema}
                     // Reinitialize and remount Formik when editing a different contract so values don't persist
                     enableReinitialize={true}
                     key={`${id}-${contractUpdatePrefilledData?.id || ''}`}
                     onReset={() => {
                        setActiveStep(0);
                     }}
                  >
                     {({ isSubmitting, values, setFieldValue, setFieldTouched, touched, errors, handleBlur }) => {
                        // Capture setFieldValue reference for restoration
                        setFieldValueRef.current = setFieldValue;

                        return (
                           <Form>
                              {isFormSubmitting && (
                                 <div
                                    style={{
                                       position: 'fixed',
                                       top: 0,
                                       left: 0,
                                       right: 0,
                                       bottom: 0,
                                       display: 'flex',
                                       flexDirection: 'column',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       backgroundColor: 'rgba(255,255,255,0.8)',
                                       zIndex: 9999,
                                    }}
                                 >
                                    <CircularProgress />
                                    <p style={{ marginTop: '16px' }}>
                                       Submitting contract. Please wait...
                                    </p>
                                 </div>
                              )}
                              <StepContent
                                 {...{
                                    isSubmitting,
                                    values,
                                    setFieldValue,
                                    touched,
                                    errors,
                                    setFieldTouched,
                                    handleBlur,
                                 }}
                              />
                              <Box
                                 sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: '10px',
                                    pt: 2,
                                 }}
                              >
                                 <Button
                                    color="inherit"
                                    disabled={activeStep === 0 || isFormSubmitting}
                                    onClick={() => {
                                       handleBack();
                                    }}
                                    sx={{ mr: 1 }}
                                    className="backBtn"
                                 >
                                    Back
                                 </Button>
                                 {activeStep === steps.length - 1 ? (
                                    <Button
                                       type="button"
                                       onClick={() => {
                                          handleSubmit(values);
                                       }}
                                       disabled={isSubmitting || isFormSubmitting}
                                       className="commonBtn"
                                    >
                                       Submit
                                    </Button>
                                 ) : (
                                    <Button
                                       type="button"
                                       onClick={() => {
                                          handleNext(values);
                                       }}
                                       disabled={isSubmitting || isFormSubmitting}
                                       className="commonBtn"
                                    >
                                       Next
                                    </Button>
                                 )}
                              </Box>
                           </Form>
                        );
                     }}
                  </Formik>
               </Box>
            </div>
         </div>
      </StyledDashboardLayout>
   );
}

// Helper: preserve user-entered spacing. Convert &nbsp; entity to NBSP char and do not collapse/trim spaces.
function normalizeEditorContent(value = '') {
   return String(value).replace(/&nbsp;/g, '\u00A0');
}
